//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// onboarding-org-invite imports
import { fragments, refetchQuery } from "./OnboardingOrgInvite.gql";
import { IContainer, IProps, IState, AugmentedPersonMap } from "./OnboardingOrgInvite.d";
import * as s from "./OnboardingOrgInvite.styles";
// other internals
import { CopyPersonalInviteLinkBtn } from "@components/copy-personal-invite-link-btn";
import { OrgInviteCheckbox } from "../org-invite-checkbox";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { track, TrackingEvents } from "@util/analytics.util";
import { compareBooleans, compareStrings } from "@util/sort.util";
import { IReduxState } from "@reducers/root.reducer";
import { isOnPersonalizedInviteLinks } from "@util/experiment.util";
import { recordImpression } from "@mutations";
import { EXPERIMENT_FLAGS } from "@util/experiment.util";

// material-ui
import { Button, InputAdornment, List, TextField } from "@material-ui/core";
import { Check, Search } from "@material-ui/icons";

//////////////////
// COMPONENT
//////////////////
export class OnboardingOrgInviteComponent extends React.Component<IProps, IState> {
  public static ClipCount = 5;

  constructor(props: IProps) {
    super(props);

    const currentPersons = this.getAugmentedPersons(props);
    this.state = {
      currentPersons,
      recentlySentPersons: {},
      query: "",
      loading: true,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const currentPersons = this.getAugmentedPersons(nextProps);
    const newState: Partial<IState> = {
      currentPersons,
    };
    if (Object.keys(currentPersons).length && this.state.loading) {
      newState.loading = false;
    }
    this.setState(newState as IState);
  }

  public componentDidMount() {
    this.setState({ loading: false }, () => {
      if (this.props.onInvitesLoaded) {
        this.props.onInvitesLoaded();
      }
    });

    // count + track suggested invites
    const persons = this.state.currentPersons;
    const suggestedPersons = this.sortPersons(persons)
      .slice(0, 20) // for perf reasons, only render 10
      .filter((calId) => persons[calId].isSuggested && !persons[calId].isUser);
    track(TrackingEvents.ONBOARDING.ONBOARDING_INVITES_SUGGESTED_INVITEES_COUNT, {
      count: suggestedPersons.length,
    });

    // personalized links experiment
    const { org, relay } = this.props;
    recordImpression(
      relay.environment,
      org,
      EXPERIMENT_FLAGS.personalizedInviteLinks,
      () => null,
      () => null,
    );
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getAugmentedPersons = (props: IProps) => {
    const org = props.org;
    const suggestedInviteMap = this.getSuggestedInviteMap(props);
    const pendingInviteMap = this.getPendingInviteMap(props);
    const recentlySentPersons = (this.state && this.state.recentlySentPersons) || {};
    const query = this.state && this.state.query;

    // sanity check
    if (!org || !org.suggestedInvites || !org.suggestedInvites.edges) {
      return {} as AugmentedPersonMap;
    }

    // use either query result or corpus
    let persons: ISchema.IPerson[] = [];
    if (query) {
      const personQuery = props.viewer.personQueryResultErrorable;
      if (personQuery.__typename !== "PersonQueryResult") {
        return {} as AugmentedPersonMap;
      }
      persons = personQuery.persons;
    } else {
      const personCorpus = props.org.personCorpusErrorable;
      if (personCorpus.__typename !== "PersonList") {
        return {} as AugmentedPersonMap;
      }
      persons = personCorpus.list;

      // if a teammate leaves the org, their person won't return
      // check current teammates for anybody missing and create shim persons for them
      const missingPersons: ISchema.IPerson[] = [];
      const personEmails = persons.map((p) => p.primaryEmail);
      const missingSelected = this.props.selectedCalendarIds.filter(
        (s) => personEmails.indexOf(s) === -1,
      );
      missingSelected.forEach((ms) => {
        missingPersons.push({
          primaryEmail: ms,
        } as ISchema.IPerson);
      });
      if (missingPersons.length) {
        persons = [...persons, ...missingPersons];
      }
    }

    const result: AugmentedPersonMap = persons
      .filter((person) => {
        if (!this.props.showCurrentUser && person.isYou) {
          return false;
        }
        return true;
      })
      .reduce((accumulator: AugmentedPersonMap, nextPerson): AugmentedPersonMap => {
        const { profile, primaryEmail, isYou, isUser } = nextPerson;
        const isRecentlySent = !!recentlySentPersons[primaryEmail];
        const isPending = !!pendingInviteMap[primaryEmail] || isRecentlySent;

        // defaults
        accumulator[primaryEmail] = {
          isYou,
          isUser,
          isPending,
          targetCalendarId: primaryEmail,
          givenName: profile ? profile.givenName : primaryEmail, // if no user profile, show primaryEmail
          familyName: profile ? profile.familyName : "",
          externalImageUrl: profile ? profile.externalImageUrl : "",
          isSuggested: false,
          recentlySent: isRecentlySent,
          numberOfSharedMeetings: 0,
          numberOfOrganizedMeetings: 0,
          inviteReason: ISchema.SuggestedOrgInviteReason.AttendSameMeetings,
          totalScore: 0,
        };

        // handle suggested invites
        const suggestedInvite = suggestedInviteMap[primaryEmail];
        if (!!suggestedInvite) {
          accumulator[primaryEmail].isSuggested = true;
          accumulator[primaryEmail].numberOfSharedMeetings = suggestedInvite.numberOfSharedMeetings;
          accumulator[primaryEmail].numberOfOrganizedMeetings =
            suggestedInvite.numberOfOrganizedMeetings;
          accumulator[primaryEmail].totalScore = suggestedInvite.totalScore;
          accumulator[primaryEmail].inviteReason = suggestedInvite.inviteReason;
        }

        return accumulator;
      }, {});

    // include any recently sent person if there's no query
    if (!query) {
      Object.keys(recentlySentPersons).forEach((cId: string) => {
        result[cId] = recentlySentPersons[cId];
      });
    }

    return result;
  };

  private getSuggestedInviteMap = (
    props: IProps,
  ): { [calId: string]: ISchema.ISuggestedOrgInvite } => {
    const suggestedInvites = props.org.suggestedInvites;
    const suggestedInviteMap: { [calId: string]: ISchema.ISuggestedOrgInvite } = {};
    if (!suggestedInvites || !suggestedInvites.edges) {
      return suggestedInviteMap;
    }

    suggestedInvites.edges.forEach((edge) => {
      suggestedInviteMap[edge.node.targetCalendarId] = edge.node;
    });

    return suggestedInviteMap;
  };

  private getPendingInviteMap = (props: IProps): { [calId: string]: ISchema.IOrgInvite } => {
    const pendingInvites = props.org.pendingInvites;
    const pendingInviteMap: { [calId: string]: ISchema.IOrgInvite } = {};
    if (!pendingInvites || !pendingInvites.edges) {
      return pendingInviteMap;
    }

    pendingInvites.edges.forEach((edge) => {
      pendingInviteMap[edge.node.targetCalendarId] = edge.node;
    });

    return pendingInviteMap;
  };

  private sortPersons = (persons: AugmentedPersonMap) => {
    return Object.keys(persons).sort((a, b) => {
      const personA = persons[a];
      const personB = persons[b];

      return (
        compareBooleans(personA.isYou, personB.isYou) || // you go to the bottom
        compareBooleans(personA.isUser, personB.isUser) || // users go to the bottom
        (personA.isSuggested && personB.isSuggested && personB.totalScore - personA.totalScore) || // best suggestions to the top
        compareBooleans(personA.isSuggested, personB.isSuggested, false) || // suggestions to the top (note isAscending = false)
        compareBooleans(personA.recentlySent, personB.recentlySent) ||
        compareBooleans(personA.isPending, personB.isPending) ||
        compareStrings(personA.givenName, personB.givenName) ||
        compareStrings(personA.familyName, personB.familyName)
      );
    });
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    this.props.relay.refetch({
      orgRelayId: this.props.org.id,
      queryInput: query,
      excludeCalendars: [],
    });
    this.setState({ query });
  };

  private onSelectAll = (selectAll: boolean) => {
    const trackingString = selectAll
      ? TrackingEvents.ONBOARDING.ONBOARDING_INVITES_SELECT_ALL_CLICKED
      : TrackingEvents.ONBOARDING.ONBOARDING_INVITES_UNSELECT_ALL_CLICKED;
    track(trackingString);

    const persons = this.state.currentPersons;
    this.sortPersons(persons)
      .slice(0, 20) // for perf reasons, only render 10
      .forEach((calId) => {
        if (!persons[calId].isUser) {
          // select/unselect if they are not already a user
          this.props.onAddInvitee(calId, selectAll, persons[calId].isSuggested);
        }
      });
  };

  private isAtLeastOnePersonNotSelectedAndNotUser = () => {
    const persons = this.state.currentPersons;
    return this.sortPersons(persons)
      .slice(0, 20) // for perf reasons, only render 10
      .some(
        (calId) => !persons[calId].isUser && this.props.selectedCalendarIds.indexOf(calId) === -1,
      );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderPersons() {
    const { selectedCalendarIds } = this.props;
    const persons = this.state.currentPersons;

    if (!Object.keys(persons).length) {
      return <div style={s.styles.empty}>No results found</div>;
    }

    // don't include users who have already joined
    const newPersons = Object.keys(persons)
      .filter((key) => persons[key].isUser == false)
      .reduce((obj: any, key: string) => {
        obj[key] = persons[key];
        return obj;
      }, {});

    return this.sortPersons(newPersons)
      .slice(0, 20) // for perf reasons, only render 10
      .map((calId, i) => {
        return (
          <OrgInviteCheckbox
            key={i}
            person={{ ...persons[calId] }}
            checked={selectedCalendarIds.indexOf(calId) > -1}
            onCheck={this.props.onAddInvitee}
          />
        );
      });
  }

  public render() {
    const { viewer, org } = this.props;
    if (this.state.loading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}>
          <CircularProgress />
        </div>
      );
    }

    const marginCorrect = !!this.props.marginCorrect
      ? {
          marginLeft: this.props.marginCorrect,
          marginRight: this.props.marginCorrect,
        }
      : {};

    const showSelectAll = this.isAtLeastOnePersonNotSelectedAndNotUser();
    return (
      <div>
        <div style={s.styles.textFieldContainer}>
          <TextField
            label="Search for teammates"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ width: 20, height: 20 }} />
                </InputAdornment>
              ),
            }}
            value={this.state.query}
            onChange={this.onSearch}
            style={s.styles.textField}
          />
          {isOnPersonalizedInviteLinks(org) ? (
            <CopyPersonalInviteLinkBtn org={org} user={viewer.user} />
          ) : (
            <Button
              style={s.styles.selectAllButton}
              onClick={() => this.onSelectAll(showSelectAll)}
            >
              <Check />
              <span style={s.styles.selectAllLabel}>
                {showSelectAll ? "Select all" : "Unselect all"}
              </span>
            </Button>
          )}
        </div>
        <List style={{ ...marginCorrect }}>{this.renderPersons()}</List>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {})(OnboardingOrgInviteComponent);

//////////////////
// RELAY
//////////////////
export const OnboardingOrgInvite = createRefetchContainer(reduxContainer, fragments, refetchQuery);
